import { useAxleMutation, useAxleQuery } from '@/fetching/apiHelpers';
import { organizationBasicSchema, territorySimple } from '@/fetching/sharedSchemas/basicSchemas';
import { qualificationBasicSchema } from '@/fetching/sharedSchemas/qualificationSchemas';
import _ from 'lodash';
import { z } from 'zod';

const handshakeWithPermissionsSchema = z.object({
    uuid: z.string(),
    from_org: organizationBasicSchema,
    to_org: organizationBasicSchema,
    has_territory_restriction: z.boolean(),
    territories: z.array(territorySimple),
    permissions: z.object({
        write: z.boolean(),
    }),
});
const supplyConnectionSchema = z.object({
    handshake: handshakeWithPermissionsSchema,
    org_qualifications: z.array(qualificationBasicSchema),
    passthrough_qualifications: z.array(qualificationBasicSchema),
});
export type SupplyConnection = z.infer<typeof supplyConnectionSchema>;
export const useSupplyConnections = () =>
    useAxleQuery({
        queryKey: ['connections', 'supply', 'all'],
        path: '/downstream-handshakes',
        suspense: true,
        responseSchema: z.object({
            downstream_handshakes_with_qualifications: z.array(supplyConnectionSchema),
        }),
        transform: (data) => ({
            allSupplyConnections: _.sortBy(data.downstream_handshakes_with_qualifications, (sc) => [
                sc.handshake.from_org.name,
                sc.handshake.to_org.name,
            ]),
        }),
    }).data;
export const useEditSupplyConnectionOptions = (supplyConnectionId: string) =>
    useAxleQuery({
        queryKey: ['connections', 'supply', supplyConnectionId, 'options'],
        path: '/downstream-handshakes/edit/options',
        params: {
            handshake_uuid: supplyConnectionId,
        },
        suspense: true,
        responseSchema: z.object({
            org_qualification_options: z.array(qualificationBasicSchema),
            passthrough_qualification_options: z.array(qualificationBasicSchema),
            territory_options: z.array(territorySimple),
        }),
        transform: (data) => ({
            orgLevelQualificationOptions: data.org_qualification_options,
            passthroughQualificationOptions: data.passthrough_qualification_options,
            territoryOptions: data.territory_options,
        }),
    }).data;
export const useEditSupplyConnection = (supplyConnectionId: string) =>
    useAxleMutation({
        axiosConfig: (data: Record<string, unknown>) => ({
            method: 'post',
            path: '/downstream-handshakes/edit',
            data: {
                handshake_uuid: supplyConnectionId,
                ...data,
            },
        }),
        softResetQueries: [['connections', 'supply']],
    });
const demandConnectionSchema = z.object({
    handshake: handshakeWithPermissionsSchema,
    qualification_requirements: z.array(qualificationBasicSchema),
});
export type DemandConnection = z.infer<typeof demandConnectionSchema>;
export const useDemandConnections = () =>
    useAxleQuery({
        queryKey: ['connections', 'demand', 'all'],
        path: '/upstream-handshakes',
        suspense: true,
        responseSchema: z.object({
            upstream_handshakes: z.array(demandConnectionSchema),
        }),
        transform: (data) => ({
            allDemandConnections: _.sortBy(data.upstream_handshakes, (dc) => [
                dc.handshake.from_org.name,
                dc.handshake.to_org.name,
            ]),
        }),
    }).data;
export const useEditDemandConnectionOptions = (demandConnectionId: string) =>
    useAxleQuery({
        queryKey: ['connections', 'demand', demandConnectionId, 'options'],
        path: '/upstream-handshakes/edit/options',
        params: {
            handshake_uuid: demandConnectionId,
        },
        suspense: true,
        responseSchema: z.object({
            qualification_options: z.array(qualificationBasicSchema),
        }),
        transform: (data) => ({
            qualificationOptions: data.qualification_options,
        }),
    }).data;
export const useEditDemandConnection = (demandConnectionId: string) =>
    useAxleMutation({
        axiosConfig: ({ qualificationIds }: { qualificationIds: string[] }) => ({
            method: 'post',
            path: '/upstream-handshakes/edit',
            data: {
                handshake_uuid: demandConnectionId,
                qualification_uuids: qualificationIds,
            },
        }),
        softResetQueries: [['connections'], ['demand']],
    });
export const useCreateConnectionOptions = () =>
    useAxleQuery({
        queryKey: ['connections', 'create'],
        path: '/handshakes/create/options',
        suspense: true,
        responseSchema: z.object({
            from_orgs: z.array(organizationBasicSchema),
            to_orgs: z.array(organizationBasicSchema),
        }),
        transform: (data) => ({
            fromOrgs: data.from_orgs,
            toOrgs: data.to_orgs,
        }),
    }).data;
export const useCreateConnection = () =>
    useAxleMutation({
        axiosConfig: ({ fromOrgId, toOrgId }: { fromOrgId: string; toOrgId: string }) => ({
            method: 'post',
            path: '/handshakes/create',
            data: {
                from_org_uuid: fromOrgId,
                to_org_uuid: toOrgId,
            },
        }),
        softResetQueries: [['connections']],
    });
